import { ColumnData } from '../models/column-data.model';
import { VariableTypeEnum } from '../../../shared/ontology/variable-type.enum';

type ColumnsStorageKeyPrefix =
    typeof ColumnDataService.storageKeyPrefixOrderState |
    typeof ColumnDataService.storageKeyPrefixSelectedState;

type ColumnsStorageKeys =
    typeof ColumnDataService.GERMPLASM_CHECKS_KEY |
    typeof ColumnDataService.CHANGE_PLOT_ENTRY_KEY |
    typeof ColumnDataService.CROSSES_AND_SELECTIONS_TABLE_KEY |
    typeof ColumnDataService.STUDY_DATASET_KEY |
    typeof ColumnDataService.STUDY_DESIGN_KEY |
    typeof ColumnDataService.INVENTORY_TABLE_COLUMNS_KEY |
    typeof ColumnDataService.SAMPLE_GENOTYPES_KEY |
    typeof ColumnDataService.SAMPLE_LIST_TABLE_COLUMNS_KEY |
    typeof ColumnDataService.STUDY_ENVIRONMENTS_KEY |
    typeof ColumnDataService.SUMMARY_STATISTICS_KEY;


export class ColumnDataService {

    public static readonly GERMPLASM_CHECKS_KEY = 'germplasm-checks-';
    public static readonly CHANGE_PLOT_ENTRY_KEY = 'change-plot-entry-';
    public static readonly CROSSES_AND_SELECTIONS_TABLE_KEY = 'crosses-and-selections-table-';
    public static readonly STUDY_DATASET_KEY = 'study-dataset-';
    public static readonly STUDY_DESIGN_KEY = 'study-design-';
    public static readonly INVENTORY_TABLE_COLUMNS_KEY = 'inventory-table-columns-';
    public static readonly SAMPLE_GENOTYPES_KEY = 'sample-genotypes-';
    public static readonly SAMPLE_LIST_TABLE_COLUMNS_KEY = 'sample-list-table-columns-';
    public static readonly STUDY_ENVIRONMENTS_KEY = 'study-environments-';
    public static readonly SUMMARY_STATISTICS_KEY = 'summary-statistics-';

    public HIDE_FROM_TABLE = [];

    // Order and selected state are persisted independently.
    public static readonly storageKeyPrefixOrderState = 'dataset-columns-order';
    public static readonly storageKeyPrefixSelectedState = 'dataset-columns-selected';

    private readonly orderStateStorageKey: string;
    private readonly selectedStateStorageKey: string;

    columnDataByInputTermId: { [key: number]: ColumnData[]} = {};

    displayedColumns: ColumnData[] = [];

    constructor(storageKey: ColumnsStorageKeys, id: string) {
        this.orderStateStorageKey = ColumnDataService.generateKey(ColumnDataService.storageKeyPrefixOrderState, `${storageKey}-${id}`);
        this.selectedStateStorageKey = ColumnDataService.generateKey(ColumnDataService.storageKeyPrefixSelectedState, `${storageKey}-${id}`);
    }

    processColumns(columns: ColumnData[], previousColums: ColumnData[]): ColumnData[] {

        // Store the current state of columns before processing the new ones
        const previousColumnsState = {};
        if (previousColums && previousColums.length) {
            previousColums.forEach(col => {
                // Use termId (or another unique identifier) as the key
                previousColumnsState[col.termId] = {
                    visible: col.visible,
                    selected: col.selected,
                    // Save column filters
                    isFiltered: col.isFiltered,
                    query: col.query,
                    sortingAsc : col.sortingAsc,
                    possibleValues: col.possibleValues,
                    isSelectAll: col.isSelectAll
                };
            });
        }

        const columnsData = [...columns];
        const tempColumnsData = [];

        columnsData.forEach(column => {
            const columnData: ColumnData = {
                ...column,
                displayName: column.alias || column.name || column.displayName,
                visible: previousColumnsState[column.termId]?.visible ?? column.visible,
                selected: previousColumnsState[column.termId]?.selected ?? column.selected,
                variableTypeId: VariableTypeEnum[column.variableType] || null,
                id: column.id,
            };

            if (columnData.possibleValues) {
                columnData.possibleValuesByName = {};
                columnData.possibleValuesById = {};
                columnData.possibleValues.forEach((possibleValue) => {
                    // so we can use "Please Choose"=empty value
                    possibleValue.displayValue = possibleValue.name;
                    // convenience map to avoid looping later
                    columnData.possibleValuesByName[possibleValue.name] = possibleValue;
                    columnData.possibleValuesById[possibleValue.id.toString()] = possibleValue;
                });
            }

            // store formula info to update out-of-sync status after edit
            if (columnData.formula && columnData.formula.inputs) {
                columnData.formula.inputs.forEach((input) => {
                    if (!this.columnDataByInputTermId[input.id]) {
                        this.columnDataByInputTermId[input.id] = [];
                    }
                    this.columnDataByInputTermId[input.id].push(columnData);
                });
            }

            // Restore column filters
            columnData.isFiltered = previousColumnsState[column.termId]?.isFiltered;
            columnData.query = previousColumnsState[column.termId]?.query;
            columnData.sortingAsc = previousColumnsState[column.termId]?.sortingAsc;
            if (previousColumnsState[column.termId]?.possibleValues?.length > 0) {
                columnData.possibleValues = previousColumnsState[column.termId].possibleValues;
            }
            columnData.isSelectAll = previousColumnsState[column.termId]?.isSelectAll;

            tempColumnsData.push(columnData);
        });

        this.displayedColumns = tempColumnsData.filter(col => {
            return !this.HIDE_FROM_TABLE.includes(col.termId);
        });
        return this.displayedColumns;

    }

    saveColumnsState(): void {
        if (!this.displayedColumns || !this.getOrderStateStorageKey() || !this.getSelectedStateStorageKey()) {
            return;
        }

        const columnOrder: number[] = this.displayedColumns.map(col => col.termId);
        const selectedByTermId: { [termId: number]: boolean } = {};
        this.displayedColumns.forEach((col) => {
            selectedByTermId[col.termId] = !!col.selected;
        });

        localStorage.setItem(this.getOrderStateStorageKey(), JSON.stringify(columnOrder));
        localStorage.setItem(this.getSelectedStateStorageKey(), JSON.stringify(selectedByTermId));
    }

    getVisibleColumns() {
        const visibleColumns = [];
        this.displayedColumns.forEach((column, index)=> {
            if (column && column.name) {
                if (column.visible && column.selected && !this.HIDE_FROM_TABLE.includes(column.termId)) {
                    // Add the actual name of the column, not the alias.
                    visibleColumns.push(column.name);
                }
            }
        });
        return visibleColumns;
    }

    // Retrieve and apply saved column order and selection state
    // Return the updated displayedColumns with applied state
    applyColumnsState(): ColumnData[] {
        if (!this.getOrderStateStorageKey() || !this.getSelectedStateStorageKey()) {
            return;
        }

        const savedOrderRaw = localStorage.getItem(this.getOrderStateStorageKey());
        const savedSelectedRaw = localStorage.getItem(this.getSelectedStateStorageKey());

        if (!savedOrderRaw && !savedSelectedRaw) {
            // If no saved state exists, persist current state and return.
            this.saveColumnsState();
            return this.displayedColumns;
        }

        try {
            let orderedColumns = this.displayedColumns;
            let orderLoaded = false;
            let selectedLoaded = false;

            if (savedOrderRaw) {
                const termIdOrder = JSON.parse(savedOrderRaw) as unknown;
                if (Array.isArray(termIdOrder) && termIdOrder.every((t) => typeof t === 'number')) {
                    orderLoaded = true;
                    const nextOrderedColumns: ColumnData[] = [];

                    (termIdOrder as number[]).forEach((termId) => {
                        const column = this.displayedColumns.find(col => col.termId === termId);
                        if (column) {
                            nextOrderedColumns.push(column);
                        }
                    });

                    this.displayedColumns.forEach(column => {
                        if (!nextOrderedColumns.find(col => col.termId === column.termId)) {
                            nextOrderedColumns.push(column);
                        }
                    });

                    orderedColumns = nextOrderedColumns;
                }
            }

            if (savedSelectedRaw) {
                const selectedByTermId = JSON.parse(savedSelectedRaw) as unknown;
                if (selectedByTermId && typeof selectedByTermId === 'object') {
                    selectedLoaded = true;
                    orderedColumns.forEach((col) => {
                        const value = (selectedByTermId as any)[col.termId];
                        if (typeof value === 'boolean') {
                            col.selected = value;
                        }
                    });
                }
            }

            this.displayedColumns = orderedColumns;

            // Ensure both keys exist and are in the expected format going forward.
            if (!orderLoaded || !selectedLoaded) {
                this.saveColumnsState();
            }

            return this.displayedColumns;
        } catch (e) {
            console.error('Error loading column state:', e);
        }
    }

    deleteColumnsStateForCurrentKey(): void {
        this.deleteColumnsOrderState();
        this.deleteColumnsSelectedState();
    }

    deleteColumnsOrderState(): void {
        localStorage.removeItem(this.getOrderStateStorageKey());
    }

    deleteColumnsSelectedState(): void {
        localStorage.removeItem(this.getSelectedStateStorageKey());
    }

    public static deleteColumnsStateByStorageKey(storageKeyPrefix:  ColumnsStorageKeyPrefix, storageKey: ColumnsStorageKeys) {
        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (!key) {
                continue;
            }
            if (key.startsWith(this.generateKey(storageKeyPrefix, storageKey))) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));
    }

    public static deleteColumnsStateForAllStorageKeys() {
        const prefixes = [
            ColumnDataService.storageKeyPrefixOrderState,
            ColumnDataService.storageKeyPrefixSelectedState,
        ];

        const keysToRemove: string[] = [];
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (!key) {
                continue;
            }
            if (prefixes.some((prefix) => key.startsWith(prefix))) {
                keysToRemove.push(key);
            }
        }
        keysToRemove.forEach((key) => localStorage.removeItem(key));
    }

    private static generateKey(storageKeyPrefix: ColumnsStorageKeyPrefix, storageKey: string) {
        return `${storageKeyPrefix}-${storageKey}`;
    }

    private getOrderStateStorageKey(): string {
        return this.orderStateStorageKey;
    }

    private getSelectedStateStorageKey(): string {
        return this.selectedStateStorageKey;
    }

}
